import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_CATALOG_KEYS } from "@shared/constants/queryKeys";
import type { PaginatedResponse, PaginationMeta } from "@shared/types";
import type { Catalog, CatalogFilters } from "@admin/types/catalog";

type CatalogListResponse = {
  success?: boolean;
  message?: string;
  data:
    | Catalog[]
    | {
        items: Catalog[];
        pagination?: PaginationMeta;
      };
  pagination?: PaginationMeta;
  meta?: PaginationMeta;
};

export function useCatalogs(params: CatalogFilters = {}) {
  const {
    page = 1,
    limit = 10,
    active,
    featured,
    name,
    sortField = "order",
    sortDirection = "asc",
    includeDeleted = false,
  } = params;

  return useQuery({
    queryKey: ADMIN_CATALOG_KEYS.list({ page, limit, active, featured, name, sortField, sortDirection, includeDeleted }),
    queryFn: async (): Promise<PaginatedResponse<Catalog>> => {
      const searchParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (active !== undefined) {
        searchParams.append("active", String(active));
      }
      if (featured !== undefined) {
        searchParams.append("featured", String(featured));
      }
      if (name) {
        searchParams.append("name", name);
      }
      if (sortField) {
        searchParams.append("sortField", sortField);
      }
      if (sortDirection) {
        searchParams.append("sortDirection", sortDirection);
      }
      if (includeDeleted) {
        searchParams.append("includeDeleted", String(includeDeleted));
      }

      const response = await apiClient.get<CatalogListResponse>(
        `/catalog?${searchParams.toString()}`
      );

      const rawData = response.data;
      const items = Array.isArray(rawData)
        ? rawData
        : rawData?.items ?? [];

      const meta =
        response.pagination ??
        response.meta ??
        (Array.isArray(rawData) ? undefined : rawData?.pagination);

      const computedLimit = meta?.limit ?? limit;
      const computedPage = meta?.page ?? page;
      const computedTotal = meta?.total ?? items.length;
      const computedTotalPages =
        meta?.totalPages ??
        Math.max(1, Math.ceil(computedTotal / computedLimit));

      const pagination: PaginationMeta = {
        total: computedTotal,
        page: computedPage,
        limit: computedLimit,
        totalPages: computedTotalPages,
        hasNext: meta?.hasNext ?? computedPage < computedTotalPages,
        hasPrev: meta?.hasPrev ?? computedPage > 1,
      };

      return {
        success: response.success ?? true,
        message: response.message,
        data: items,
        pagination,
      };
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useCatalog(id: string) {
  return useQuery({
    queryKey: ADMIN_CATALOG_KEYS.detail(id),
    queryFn: async (): Promise<Catalog> => {
      const response = await apiClient.get<{ success?: boolean; data: Catalog; message?: string }>(
        `/catalog/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

